import { useMemo } from 'react';

import { invariant } from '@spotify/confidence-core-react';
import {
  StatsDataFragment,
  StatsV2AnalysisMetricResultStatus,
} from '@spotify/confidence-graphql';
import {
  useIsStatsModuleStrategySequentialV2,
  useWorkflowInstanceContext,
} from '@spotify/plugin-confidence-workflows-react';

import { convertAnalysisResults } from '../helpers';
import { AnalysisResult, StatsData2 } from '../types';

type UseStatsDataOptions = {
  exposureFilter?: string;
};

const emptyAnalysisResult: AnalysisResult = {
  results: [],
  annotations: {},
  recommendations: [],
};

const resultStatusesWithoutOutput = [
  StatsV2AnalysisMetricResultStatus.MetricResultStatusWaitForData,
  StatsV2AnalysisMetricResultStatus.MetricResultStatusWaitForEnd,
];

const hasResultOutput = (analysisResult: AnalysisResult): boolean =>
  Object.values(analysisResult.annotations).some(
    annotations => annotations.length > 0,
  ) ||
  analysisResult.results.some(hypothesisResult =>
    hypothesisResult.result.some(
      comparisonResult =>
        Boolean(comparisonResult.lastResult) ||
        comparisonResult.result.length > 0 ||
        (comparisonResult.status?.status &&
          !resultStatusesWithoutOutput.includes(
            comparisonResult.status.status,
          )),
    ),
  );

const selectAnalysisResult = (
  analysisResults: AnalysisResult[],
  exposureFilter: string,
): AnalysisResult =>
  analysisResults.length === 1
    ? analysisResults[0]
    : (analysisResults.find(
        result => result.exposureFilter === exposureFilter,
      ) ?? emptyAnalysisResult);

export const useStatsData = ({
  exposureFilter = 'base',
}: UseStatsDataOptions = {}): StatsData2 => {
  const { hasHadState, hasLaunched, isLive, moduleData2, state } =
    useWorkflowInstanceContext();

  const statsData: StatsDataFragment | undefined | null = moduleData2?.stats;
  invariant(statsData, 'useStatsData: stats data is required');

  const analysisResults = useMemo(
    () => convertAnalysisResults(statsData.analysisResults),
    [statsData.analysisResults],
  );
  const selectedAnalysisResult = useMemo(
    () => selectAnalysisResult(analysisResults, exposureFilter),
    [analysisResults, exposureFilter],
  );
  const isSequential = useIsStatsModuleStrategySequentialV2(statsData);
  const isFixedHorizon = !isSequential;
  const hasOutput = hasResultOutput(selectedAnalysisResult);
  const hasBeenActive = hasHadState(['active']);
  const isActive = state === 'active';
  const areResultsExpected =
    isActive ||
    (hasBeenActive && hasOutput) ||
    (hasLaunched && (isSequential || (isFixedHorizon && !isLive)));
  return {
    analysisResults,
    selectedAnalysisResult,
    isSequential,
    isFixedHorizon,
    areResultsExpected,
    hasResultsToShow: areResultsExpected && hasOutput,
  };
};
